import type { Optional } from '@festina-life/core'
import type { ParserRuleContext, Token } from 'antlr4'

import FPlusParser, {
  type ExpressionContext,
  ListLiteralContext,
} from '../lexer/ANTLR/FPlusParser'
import { parseFPlusExpressionScript } from './fplusParser'
import { findCollectionNode } from './helpers'
import { nonWhiteSpaceTokens as ns, tokenize } from './tokenizer'
import type { CollectionLiteralExtraction, FPlusLiteralType } from './types'

const MINUS_SIGN_CODE = 18

// moved to tokenizer.ts

function hasToken(tokens: Token[], type: number): boolean {
  return tokens.some((t) => t.type === type)
}

function countToken(tokens: Token[], type: number): number {
  return tokens.filter((t) => t.type === type).length
}

// moved to tokenizer.ts

function inferBlankOrNull(trimmedCode: string): Optional<FPlusLiteralType> {
  if (trimmedCode === '') {
    return 'blank'
  }
  if (trimmedCode === 'null') {
    return 'null'
  }
  return undefined
}

function inferFraction(
  nonWhiteSpaceTokens: Token[],
  tokens: Token[],
): Optional<FPlusLiteralType> {
  if (!hasToken(tokens, FPlusParser.PERCENT)) {
    return undefined
  }
  const percentCount = countToken(tokens, FPlusParser.PERCENT)
  const hasNumber =
    hasToken(tokens, FPlusParser.IntegerLiteral) ||
    hasToken(tokens, FPlusParser.DecimalLiteral)

  const allowedForFraction = new Set<number>([
    MINUS_SIGN_CODE,
    FPlusParser.IntegerLiteral,
    FPlusParser.DecimalLiteral,
    FPlusParser.PERCENT,
  ])
  const hasDisallowed = nonWhiteSpaceTokens.some(
    (t) => !allowedForFraction.has(t.type),
  )

  if (percentCount === 1 && hasNumber && !hasDisallowed) {
    if (
      nonWhiteSpaceTokens.length === 2 &&
      (nonWhiteSpaceTokens[0].type === FPlusParser.IntegerLiteral ||
        nonWhiteSpaceTokens[0].type === FPlusParser.DecimalLiteral) &&
      nonWhiteSpaceTokens[1].type === FPlusParser.PERCENT
    ) {
      return 'fraction'
    }
    if (
      nonWhiteSpaceTokens.length === 3 &&
      nonWhiteSpaceTokens[0].type === MINUS_SIGN_CODE &&
      (nonWhiteSpaceTokens[1].type === FPlusParser.IntegerLiteral ||
        nonWhiteSpaceTokens[1].type === FPlusParser.DecimalLiteral) &&
      nonWhiteSpaceTokens[2].type === FPlusParser.PERCENT
    ) {
      return 'fraction'
    }
  }
  return undefined
}

function inferString(tokens: Token[]): Optional<FPlusLiteralType> {
  if (
    !hasToken(tokens, FPlusParser.QUOTE_OPEN) &&
    !hasToken(tokens, FPlusParser.SINGLE_QUOTE_OPEN)
  ) {
    return undefined
  }
  const quoteOpenCount =
    countToken(tokens, FPlusParser.QUOTE_OPEN) +
    countToken(tokens, FPlusParser.SINGLE_QUOTE_OPEN)
  const quoteCloseCount =
    countToken(tokens, FPlusParser.QUOTE_CLOSE) +
    countToken(tokens, FPlusParser.SINGLE_QUOTE_CLOSE)
  const hasOtherTokens = tokens.some(
    (t) =>
      t.type !== FPlusParser.QUOTE_OPEN &&
      t.type !== FPlusParser.SINGLE_QUOTE_OPEN &&
      t.type !== FPlusParser.QUOTE_CLOSE &&
      t.type !== FPlusParser.SINGLE_QUOTE_CLOSE &&
      t.type !== FPlusParser.SingleLineStrText &&
      t.type !== FPlusParser.LineStrText &&
      t.type !== FPlusParser.WS,
  )
  if (quoteOpenCount === 1 && quoteCloseCount === 1 && !hasOtherTokens) {
    return 'string'
  }
  return undefined
}

function inferBoolean(tokens: Token[]): Optional<FPlusLiteralType> {
  if (!hasToken(tokens, FPlusParser.BooleanLiteral)) {
    return undefined
  }
  const booleanCount = countToken(tokens, FPlusParser.BooleanLiteral)
  const hasOtherTokens = tokens.some(
    (t) => t.type !== FPlusParser.BooleanLiteral && t.type !== FPlusParser.WS,
  )
  if (booleanCount === 1 && !hasOtherTokens) {
    return 'boolean'
  }
  return undefined
}

function inferInteger(tokens: Token[]): Optional<FPlusLiteralType> {
  if (
    !hasToken(tokens, FPlusParser.IntegerLiteral) ||
    countToken(tokens, FPlusParser.DecimalLiteral) !== 0
  ) {
    return undefined
  }
  const integerCount = countToken(tokens, FPlusParser.IntegerLiteral)
  const hasOtherTokens = tokens.some(
    (t) =>
      t.type !== FPlusParser.IntegerLiteral &&
      t.type !== FPlusParser.WS &&
      t.type !== MINUS_SIGN_CODE,
  )
  if (integerCount === 1 && !hasOtherTokens) {
    return 'integer'
  }
  return undefined
}

function inferEnum(
  nonWhiteSpaceTokens: Token[],
  tokens: Token[],
): Optional<FPlusLiteralType> {
  if (!hasToken(tokens, FPlusParser.DOT)) {
    return undefined
  }
  const dotCount = countToken(tokens, FPlusParser.DOT)
  const hasIdentifier =
    hasToken(tokens, FPlusParser.Identifier) ||
    hasToken(tokens, FPlusParser.IdentifierOrSoftKey)
  const allowedForEnum = new Set<number>([
    FPlusParser.DOT,
    FPlusParser.Identifier,
    FPlusParser.IdentifierOrSoftKey,
  ])
  const hasDisallowed = nonWhiteSpaceTokens.some(
    (t) => !allowedForEnum.has(t.type),
  )
  if (dotCount === 1 && hasIdentifier && !hasDisallowed) {
    const isIdentifier = (t: Token) =>
      t.type === FPlusParser.Identifier ||
      t.type === FPlusParser.IdentifierOrSoftKey
    if (
      nonWhiteSpaceTokens.length === 2 &&
      nonWhiteSpaceTokens[0].type === FPlusParser.DOT &&
      isIdentifier(nonWhiteSpaceTokens[1])
    ) {
      return 'enum'
    }
    if (
      nonWhiteSpaceTokens.length === 3 &&
      isIdentifier(nonWhiteSpaceTokens[0]) &&
      nonWhiteSpaceTokens[1].type === FPlusParser.DOT &&
      isIdentifier(nonWhiteSpaceTokens[2])
    ) {
      return 'enum'
    }
  }
  return undefined
}

// constructor inference moved to constructorParser.ts

export function inferLiteralType(code: string): Optional<FPlusLiteralType> {
  // Trim whitespace before processing
  const trimmedCode = code.trim()

  const simple = inferBlankOrNull(trimmedCode)
  if (simple) {
    return simple
  }

  const tokens = tokenize(trimmedCode)
  const nonWhiteSpaceTokens = ns(tokens)

  // Check for simple fraction literals with correct order: [number]% or [-][number]%
  const fraction = inferFraction(nonWhiteSpaceTokens, tokens)
  if (fraction) {
    return fraction
  }

  // Check for simple string literals (single/double quoted, including empty strings)
  const stringType = inferString(tokens)
  if (stringType) {
    return stringType
  }

  // Check for simple boolean literals
  const boolType = inferBoolean(tokens)
  if (boolType) {
    return boolType
  }

  // Check for simple integer literals
  const intType = inferInteger(tokens)
  if (intType) {
    return intType
  }

  // Check for simple enum-like identifiers with correct order: Identifier '.' Identifier or '.' Identifier
  const enumType = inferEnum(nonWhiteSpaceTokens, tokens)
  if (enumType) {
    return enumType
  }

  return undefined
}

// Constructor extraction
// constructor parsing moved to constructorParser.ts

export function extractCollectionLiteralItems(
  code: Optional<string>,
): Optional<CollectionLiteralExtraction> {
  if (!code) {
    return undefined
  }
  const ast = parseFPlusExpressionScript(code)

  const literal = findCollectionNode(ast)
  if (!literal) {
    return undefined
  }

  const expressions: ExpressionContext[] = literal.expression_list?.() ?? []

  const items = expressions.map((ctx: ParserRuleContext): string => {
    const start = ctx.start?.start
    const stop = ctx.stop?.stop
    if (start === undefined || stop === undefined) {
      return ctx.getText()
    }
    return code.substring(start, stop + 1)
  })

  return {
    kind: literal instanceof ListLiteralContext ? 'list' : 'set',
    items,
  }
}

export const parseTypedCollection = (
  source: string,
  matchType: FPlusLiteralType,
): Optional<string[]> => {
  const extraction = extractCollectionLiteralItems(source)
  if (!extraction) {
    return undefined
  }

  const items = extraction.items
  const isCollection = items.every((value) => {
    const inferredType = inferLiteralType(value)
    return inferredType && [matchType, 'blank', 'null'].includes(inferredType)
  })
  return isCollection ? items : undefined
}
